'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState, type ComponentType } from 'react';
import {
  BarChart3,
  BookOpen,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Compass,
  FileText,
  FolderKanban,
  Gauge,
  GitMerge,
  History,
  Inbox,
  Layers,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Network,
  Shield,
  Settings,
  Sparkles,
  Target,
  Upload,
  Users,
  Workflow,
} from 'lucide-react';
import { buildExecutiveBriefsHref } from '@/lib/executive-summaries-briefs';
import { isImmersiveRoute, isHiddenSidebarRoute } from '@/lib/is-immersive-route';
import { useCompanyId } from '@/lib/company-id-context';
import { SIGN_OUT_ERROR_MESSAGE, signOut } from '@/lib/sign-out';

type NavNode = {
  id: string;
  label: string;
  description: string;
  href?: string;
  icon: ComponentType<{ className?: string }>;
  badge?: { label: string; intent: 'new' | 'count' };
  children?: NavNode[];
};

const BLUEPRINT_PROGRAM_PORTFOLIO_HREF = '/strategy-studio/blueprints';

const NAV_BLUEPRINT: NavNode[] = [
  {
    id: 'command-center',
    label: 'Command Center',
    description: 'Executive landing',
    icon: LayoutDashboard,
    children: [
      {
        id: 'daily-brief',
        label: 'Daily Brief',
        description: 'Posture + decisions',
        href: '/daily-brief',
        icon: Compass,
      },
      {
        id: 'proactive-inbox',
        label: 'Inbox',
        description: 'Pushed briefs + alerts',
        href: '/inbox',
        icon: Inbox,
      },
      {
        id: 'decision-ledger',
        label: 'Decision Ledger',
        description: 'Governed decision record',
        href: '/decision-ledger',
        icon: History,
      },
      {
        id: 'decision-console',
        label: 'Decision Console',
        description: 'Approve & activate decisions',
        href: '/decision-console',
        icon: Gauge,
      },
    ],
  },
  {
    id: 'executive-summaries',
    label: 'Executive Summaries',
    description: 'C-suite briefs',
    icon: FileText,
    children: [
      {
        id: 'executive-summaries-overview',
        label: 'Executive Brief',
        description: 'Headline + decisions',
        href: '/executive-summaries',
        icon: LayoutDashboard,
      },
      {
        id: 'executive-briefs',
        label: 'Briefs',
        description: 'Strategy, finance, org, ops, portfolio',
        href: '/executive-summaries/briefs?tab=operations',
        icon: BookOpen,
      },
      {
        id: 'executive-decision-memos',
        label: 'Decision Memos',
        description: 'Weekly decision cadence',
        href: '/executive-summaries/decision-memos',
        icon: History,
      },
      {
        id: 'executive-board-rollup',
        label: 'Board Roll-Up',
        description: 'Quarterly decision memory',
        href: '/executive-summaries/board-rollup',
        icon: BarChart3,
      },
    ],
  },
  {
    id: 'planning',
    label: 'Planning',
    description: 'Portfolio planning',
    icon: FolderKanban,
    children: [
      {
        id: 'planning-portfolio-planner',
        label: 'Portfolio Planner',
        description: 'Cut line + swaps',
        href: '/planning/portfolio-planner',
        icon: FolderKanban,
      },
      {
        id: 'planning-inbox',
        label: 'Initiatives',
        description: 'Intake + readiness',
        href: '/planning/portfolio-planner/inbox',
        icon: Inbox,
      },
      {
        id: 'planning-history',
        label: 'Plan History',
        description: 'Runs + publishes',
        href: '/planning/portfolio-planner/history',
        icon: History,
      },
    ],
  },
  {
    id: 'organization',
    label: 'Organization',
    description: 'Structure & governance',
    icon: Building2,
    children: [
      {
        id: 'org-insights',
        label: 'Health',
        description: 'Org health & constraints',
        icon: BarChart3,
      },
      {
        id: 'org-as-is-insights',
        label: 'Insights',
        description: 'Org health & constraints',
        href: '/strategy-studio/org-insights',
        icon: BarChart3,
      },
      {
        id: 'org-designer',
        label: 'Roles & Responsibilities',
        description: 'Accountabilities & role design',
        icon: Workflow,
      },
      {
        id: 'org-structure',
        label: 'Structure',
        description: 'People in seats',
        icon: Network,
      },
      {
        id: 'org-governance',
        label: 'Governance Audit',
        description: 'Compliance & controls',
        icon: Shield,
      },
      {
        id: 'org-ai-council',
        label: 'Decision Council',
        description: 'Recommendations & rationale',
        icon: Sparkles,
        badge: { label: 'BETA', intent: 'new' },
      },
    ],
  },
  {
    id: 'scenarios',
    label: 'Scenarios',
    description: 'Model org changes',
    icon: Layers,
    children: [
      {
        id: 'templates',
        label: 'Scenarios',
        description: 'Non-M&A scenarios',
        href: '/scenarios',
        icon: FileText,
      },
      {
        id: 'fusion',
        label: 'M&A Integration',
        description: 'Fusion scenarios',
        href: '/blueprint/fusion-v2',
        icon: GitMerge,
      },
    ],
  },
  {
    id: 'strategy-studio',
    label: 'Strategy Studio',
    description: 'Define strategic intent',
    icon: Lightbulb,
    badge: { label: 'NEW', intent: 'new' },
    children: [
      {
        id: 'strategies',
        label: 'Strategies',
        description: 'Active, drafts, archived',
        href: '/strategy-studio/strategies',
        icon: Target,
      },
      {
        id: 'strategy-blueprints',
        label: 'Blueprints',
        description: 'Value, operating, transformation',
        href: '/strategy-studio/strategies',
        icon: Workflow,
      },
      {
        id: 'strategy-compiler',
        label: 'Strategy Compiler',
        description: 'Compile strategy packs',
        href: '/strategy-studio/compiler?tab=compile&view=compile',
        icon: Sparkles,
      },
    ],
  },
];

const NAV_ADMIN: NavNode[] = [
  {
    id: 'imports',
    label: 'Imports',
    description: 'Org data & profiles',
    href: '/admin/org-structure',
    icon: Upload,
  },
  {
    id: 'fusion-config',
    label: 'Fusion Config',
    description: 'Primary + Target setup',
    href: '/admin/fusion-config',
    icon: GitMerge,
  },
  {
    id: 'roles',
    label: 'Roles & Access',
    description: 'User permissions',
    href: '/admin/roles-access',
    icon: Users,
  },
  {
    id: 'security-audit',
    label: 'Security Audit',
    description: 'Access & governance events',
    href: '/admin/security-audit',
    icon: History,
  },
  {
    id: 'audit',
    label: 'Audit',
    description: 'Blueprint events',
    href: '/blueprint/audit',
    icon: History,
  },
];

function resolveActiveNodeId(pathname: string): string | null {
  if (pathname.startsWith('/daily-brief')) return 'daily-brief';
  if (pathname.includes('/decision-ledger')) return 'decision-ledger';
  if (pathname.includes('/decision-console')) return 'decision-console';
  if (pathname.includes('/execution-cockpit')) return 'decision-console';
  if (pathname.startsWith('/executive-summaries/briefs')) return 'executive-briefs';
  if (pathname.startsWith('/executive-summaries/strategy-brief')) return 'executive-briefs';
  if (pathname.startsWith('/executive-summaries/decision-memo')) return 'executive-decision-memos';
  if (pathname.startsWith('/executive-summaries/decision-memos')) return 'executive-decision-memos';
  if (pathname.startsWith('/executive-summaries/portfolio-brief')) return 'executive-briefs';
  if (pathname.startsWith('/executive-summaries/board-rollup')) return 'executive-board-rollup';
  if (pathname.startsWith('/executive-summaries/finance-brief')) return 'executive-briefs';
  if (pathname.startsWith('/executive-summaries/operations-brief')) return 'executive-briefs';
  if (pathname.startsWith('/executive-summaries/org-brief')) return 'executive-briefs';
  if (pathname.startsWith('/executive-summaries')) return 'executive-summaries-overview';
  if (pathname.startsWith('/planning/portfolio-planner/history')) return 'planning-history';
  if (pathname.startsWith('/planning/portfolio-planner/inbox')) return 'planning-inbox';
  if (pathname.startsWith('/planning/portfolio-planner')) return 'planning-portfolio-planner';
  if (pathname.startsWith('/org-canvas')) return 'org-designer';
  if (pathname.startsWith('/scenarios')) return 'templates';
  if (pathname.startsWith('/blueprint/fusion/templates')) return 'templates';   
  if (pathname.startsWith('/blueprint/fusion')) return 'fusion';
  if (pathname.startsWith('/blueprint/portfolio')) return 'portfolio';
  if (pathname.startsWith('/organization/insights')) return 'org-insights';     
  if (pathname.startsWith('/organization/structure')) return 'org-structure';
  if (pathname.startsWith('/strategy-studio/org-insights')) return 'org-as-is-insights';
  if (pathname.match(/^\/strategy-studio\/strategies\/[^/]+\/org-insights(\/|$)/)) return 'org-as-is-insights';
  if (pathname.match(/^\/strategy-studio\/strategies\/[^/]+\/org-health-insights(\/|$)/)) return 'org-as-is-insights';
  if (pathname.includes('/org-canvas-v2')) return 'org-designer';
  if (pathname.includes('/org-structure')) return 'org-structure';
  if (pathname.startsWith('/strategy-studio/executive-readout')) return 'executive-briefs';
  if (pathname.startsWith('/strategy-studio/compiler')) return 'strategy-compiler';
  if (pathname.startsWith('/strategy-studio/neuro-truth')) return 'strategy-compiler';
  if (pathname.startsWith('/strategy-studio/blueprints')) return 'strategy-blueprints';
  if (pathname.match(/^\/strategy-studio\/strategies\/[^/]+\/blueprint-runs(\/|$)/)) return 'strategy-blueprints';
  if (pathname.startsWith('/strategy-studio/strategies')) return 'strategies';
  if (pathname.startsWith('/strategy-studio')) return 'strategies';
  if (pathname.startsWith('/admin/org-structure')) return 'imports';
  if (pathname.startsWith('/admin/fusion-config')) return 'fusion-config';
  if (pathname.startsWith('/admin/roles-access')) return 'roles';
  if (pathname.startsWith('/admin/security-audit')) return 'security-audit';
  if (pathname.startsWith('/blueprint/audit')) return 'audit';
  return null;
}

function readLocalStorageBool(key: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function readLocalStorageTriBool(key: string): boolean | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function readLocalStorageExpandedSections(key: string, fallback: string[]): string[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return fallback;
  } catch {
    return fallback;
  }
}

function writeLocalStorageExpandedSections(key: string, sections: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(sections));
  } catch {
    // noop
  }
}

type SidebarNavOverride = 'collapsed' | 'expanded' | null;

function isE2eNavOverrideEnabled(): boolean {
  return process.env.NEXT_PUBLIC_HELIX_E2E_MODE === 'true';
}

function parseNavOverrideFromSearch(): SidebarNavOverride {
  try {
    if (!isE2eNavOverrideEnabled()) return null;
    const params = new URLSearchParams(window.location.search);
    const value = params.get('__nav');
    if (value === 'collapsed') return 'collapsed';
    if (value === 'expanded') return 'expanded';
    return null;
  } catch {
    return null;
  }
}

type ImmersiveSidebarOverride = 'collapsed' | 'expanded' | null;

function readImmersiveOverride(): ImmersiveSidebarOverride {
  try {
    const raw = window.localStorage.getItem('helix.sidebar.immersiveOverride');
    if (raw === 'collapsed') return 'collapsed';
    if (raw === 'expanded') return 'expanded';
    return null;
  } catch {
    return null;
  }
}

function writeImmersiveOverride(nextCollapsed: boolean) {
  try {
    window.localStorage.setItem('helix.sidebar.immersiveOverride', nextCollapsed ? 'collapsed' : 'expanded');
  } catch {
    // noop
  }
}

export function AppSidebar() {
  const pathname = usePathname() ?? '';
  const activeId = useMemo(() => resolveActiveNodeId(pathname), [pathname]);
  const [isHydrated, setIsHydrated] = useState(false);
  const searchParams = useMemo(() => {
    if (!isHydrated) return new URLSearchParams();
    return new URLSearchParams(window.location.search);
    // Intentionally depend on `pathname` so this re-reads when navigation changes the URL.
  }, [isHydrated, pathname]);
  const [collapsed, setCollapsed] = useState(false);
  const [navOverride, setNavOverride] = useState<SidebarNavOverride>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'command-center',
    'executive-summaries',
    'planning',
    'scenarios',
    'organization',
    'strategy-studio',
  ]);

  const immersive = useMemo(() => isImmersiveRoute(pathname), [pathname]);
  const hiddenSidebar = useMemo(() => isHiddenSidebarRoute(pathname), [pathname]);

  const strategyIdFromPath = useMemo(() => {
    const match = pathname.match(/\/strategy-studio\/strategies\/([^/]+)/);
    return match?.[1] ?? null;
  }, [pathname]);

  useEffect(() => {
    setIsHydrated(true);

    // Mount-only: restore initial sidebar state from localStorage and apply immersive defaults.
    if (immersive) {
      const immersiveOverride = readImmersiveOverride();
      setCollapsed(immersiveOverride === 'expanded' ? false : true);
    } else {
      setCollapsed(readLocalStorageBool('helix.sidebar.collapsed', false));
    }
    setExpandedSections(
      readLocalStorageExpandedSections('helix.sidebar.expandedSections', [
        'command-center',
        'executive-summaries',
        'planning',
        'scenarios',
        'organization',
        'strategy-studio',
      ]),
    );
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    if (immersive) {
      const immersiveOverride = readImmersiveOverride();
      if (immersiveOverride === 'collapsed') {
        setCollapsed(true);
        return;
      }
      if (immersiveOverride === 'expanded') {
        setCollapsed(false);
        return;
      }
      setCollapsed(true);
      return;
    }

    const storedCollapsed = readLocalStorageTriBool('helix.sidebar.collapsed');
    setCollapsed(storedCollapsed ?? false);
  }, [immersive, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    setNavOverride(parseNavOverrideFromSearch());
  }, [isHydrated, pathname]);

  const { roles } = useCompanyId();
  const isAdmin = roles.includes('ORG_ADMIN');
  const currentSearch = searchParams.toString();
  const currentRoute = currentSearch ? `${pathname}?${currentSearch}` : pathname;

  const navHrefs = useMemo(() => {
    const strategyId =
      strategyIdFromPath ??
      (isHydrated ? window.localStorage.getItem('helix.orgInsights.lastStrategyId') : null);

    let orgInsightsHref = '/organization/insights';
    if (strategyId) {
      const qs = new URLSearchParams();
      qs.set('strategyId', strategyId);
      const query = qs.toString();
      orgInsightsHref = query ? `${orgInsightsHref}?${query}` : orgInsightsHref;
    }

    let orgCanvasHref = '/org-canvas';
    if (strategyId) {
      const qs = new URLSearchParams();
      const queryRunId = searchParams.get('runId');
      if (queryRunId) {
        qs.set('runId', queryRunId);
        const queryPatternId = searchParams.get('patternId');
        if (queryPatternId) qs.set('patternId', queryPatternId);
      }
      if (!qs.has('runId') && isHydrated) {
        const storedRunId = window.localStorage.getItem(`helix.orgInsights.lastRunId:${strategyId}`) ?? null;
        if (storedRunId) {
          qs.set('runId', storedRunId);
          const storedPatternId =
            window.localStorage.getItem(`helix.orgInsights.lastPatternId:${strategyId}:${storedRunId}`) ?? null;
          if (storedPatternId) {
            qs.set('patternId', storedPatternId);
          }
        }
      }

      const base = `/strategy-studio/strategies/${encodeURIComponent(strategyId)}/org-canvas-v2`;
      const query = qs.toString();
      orgCanvasHref = query ? `${base}?${query}` : base;
    }

    const orgStructureStrategyId =
      strategyIdFromPath ??
      (isHydrated ? window.localStorage.getItem('helix.orgStructure.lastStrategyId') : null) ??
      (isHydrated ? window.localStorage.getItem('helix.orgInsights.lastStrategyId') : null);

    const orgStructureHref = orgStructureStrategyId
      ? `/strategy-studio/strategies/${encodeURIComponent(orgStructureStrategyId)}/org-structure`
      : '/organization/structure';

    const compilerStrategyId =
      searchParams.get('strategyId') ??
      strategyIdFromPath ??
      (isHydrated ? window.localStorage.getItem('helix.neuroTruth.lastStrategyId') : null) ??
      (isHydrated ? window.localStorage.getItem('helix.orgInsights.lastStrategyId') : null);

    const trimmedCompilerStrategyId = String(compilerStrategyId ?? '').trim() || null;

    const compilerPackVersionId =
      searchParams.get('packVersionId') ??
      (trimmedCompilerStrategyId && isHydrated
        ? window.localStorage.getItem(`helix.neuroTruth.lastPackVersionId:${trimmedCompilerStrategyId}`)
        : null);

    const trimmedCompilerPackVersionId = String(compilerPackVersionId ?? '').trim() || null;

    const strategyCompilerHref = (() => {
      const qs = new URLSearchParams();
      qs.set('tab', 'compile');
      qs.set('view', 'inputs');
      if (trimmedCompilerStrategyId) qs.set('strategyId', trimmedCompilerStrategyId);
      if (trimmedCompilerPackVersionId) qs.set('packVersionId', trimmedCompilerPackVersionId);
      return `/strategy-studio/compiler?${qs.toString()}`;
    })();

    const blueprintRunsHref = BLUEPRINT_PROGRAM_PORTFOLIO_HREF;

    const portfolioBriefStrategyId =
      searchParams.get('strategyId') ??
      strategyIdFromPath ??
      (isHydrated ? window.localStorage.getItem('helix.portfolioPlanner.lastStrategyId') : null) ??
      (isHydrated ? window.localStorage.getItem('helix.orgInsights.lastStrategyId') : null);
    const trimmedPortfolioBriefStrategyId = String(portfolioBriefStrategyId ?? '').trim() || null;
    const trimmedPortfolioBriefScopeRootNodeId =
      searchParams.get('scopeRootNodeId') ??
      (trimmedPortfolioBriefStrategyId && isHydrated
        ? window.localStorage.getItem(
            `helix.portfolioPlanner.lastScopeRootNodeId:${trimmedPortfolioBriefStrategyId}`,
          )
        : null);

    const executiveBriefsHref = buildExecutiveBriefsHref({
      tab: 'operations',
      strategyId: trimmedPortfolioBriefStrategyId,
      packVersionId: trimmedCompilerPackVersionId,
      runId: searchParams.get('runId'),
      scopeRootNodeId: trimmedPortfolioBriefScopeRootNodeId,
    });

    const decisionLedgerCadenceHref = (basePath: string) => {
      if (!trimmedPortfolioBriefStrategyId) return basePath;
      const qs = new URLSearchParams();
      qs.set('strategyId', trimmedPortfolioBriefStrategyId);
      return `${basePath}?${qs.toString()}`;
    };

    return {
      orgCanvasHref,
      orgInsightsHref,
      orgStructureHref,
      decisionMemosHref: decisionLedgerCadenceHref('/executive-summaries/decision-memos'),
      boardRollupHref: decisionLedgerCadenceHref('/executive-summaries/board-rollup'),
      executiveBriefsHref,
      blueprintRunsHref,
      strategyCompilerHref,
    };
  }, [isHydrated, searchParams, strategyIdFromPath]);

  const initials = '-';

  function toggleCollapsed() {
    const effectiveCollapsed = navOverride === 'collapsed' ? true : navOverride === 'expanded' ? false : collapsed;
    const next = !effectiveCollapsed;
    setCollapsed(next);
    window.localStorage.setItem('helix.sidebar.collapsed', String(next));
    if (immersive) writeImmersiveOverride(next);
  }

  function handleSignOut() {
    setSignOutError(null);
    setSigningOut(true);
    void signOut({ next: currentRoute }).catch(() => {
      setSigningOut(false);
      setSignOutError(SIGN_OUT_ERROR_MESSAGE);
    });
  }

  function toggleSection(sectionId: string) {
    setExpandedSections((prev) => {
      const next = prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId];
      writeLocalStorageExpandedSections('helix.sidebar.expandedSections', next);
      return next;
    });
  }

  const effectiveCollapsed = navOverride === 'collapsed' ? true : navOverride === 'expanded' ? false : collapsed;

  // Completely hide sidebar for routes that have their own navigation (e.g., Fusion V2)
  if (hiddenSidebar) {
    return null;
  }

  return (
    <div
      data-testid="helix-sidebar"
      data-hydrated={isHydrated ? 'true' : 'false'}
      data-collapsed={effectiveCollapsed ? 'true' : 'false'}
      className={[
        'helix-left-nav flex min-h-screen flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200',
        effectiveCollapsed ? 'w-[72px]' : 'w-[260px]',
      ].join(' ')}
    >
      <div
        className={[
          'flex items-center border-b border-sidebar-border',
          effectiveCollapsed ? 'justify-center px-3 py-4' : 'justify-between px-4 py-4',
        ].join(' ')}
      >
        {!effectiveCollapsed ? (
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-chart-1 text-white">
              <Workflow className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-[16px] font-bold" style={{ fontFamily: 'Georgia, serif' }}>
                Helix
              </div>
              <div className="truncate text-[10px] font-medium text-muted-foreground">Workspace</div>
            </div>
          </div>
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-chart-1 text-white">
            <Workflow className="h-5 w-5" />
          </div>
        )}

        {!effectiveCollapsed ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label="Collapse sidebar"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-sidebar-border bg-transparent text-muted-foreground hover:bg-sidebar-accent"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {effectiveCollapsed ? (
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label="Expand sidebar"
          className="group relative mx-auto my-3 flex h-9 w-9 items-center justify-center rounded-lg border border-sidebar-border bg-card text-muted-foreground hover:bg-sidebar-accent"
        >
          <ChevronRight className="h-4 w-4" />
          <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            Expand sidebar
          </span>
        </button>
      ) : null}

      <nav className={['flex-1', effectiveCollapsed ? 'px-2 py-3' : 'px-3 py-3'].join(' ')}>
        {!effectiveCollapsed ? (
          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
            Blueprint
          </div>
        ) : null}

        <div className="space-y-1">
          {NAV_BLUEPRINT.map((node) => {
            const hasChildren = Boolean(node.children?.length);
            const parentActive = hasChildren
              ? node.children?.some((c) => c.id === activeId) ?? false
              : activeId === node.id;

            const Icon = node.icon;
            const badge = node.badge;

            if (effectiveCollapsed) {
              const content = (
                <div
                  className={[
                    'flex h-11 w-full items-center justify-center rounded-[10px] transition-colors',
                    parentActive ? 'bg-card' : 'text-muted-foreground hover:bg-sidebar-accent',
                  ].join(' ')}
                >
                  <Icon className={['h-5 w-5', parentActive ? 'text-chart-1' : 'text-muted-foreground'].join(' ')} />
                </div>
              );

              return node.href ? (
                <Link
                  key={node.id}
                  href={node.href}
                  data-testid={`nav-${node.id}`}
                  aria-current={parentActive ? 'page' : undefined}
                  aria-label={node.label}
                  className="group relative block"
                >
                  {content}
                  <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    {node.label}
                  </span>
                </Link>
              ) : (
                <button
                  key={node.id}
                  type="button"
                  data-testid={`nav-${node.id}`}
                  onClick={() => toggleSection(node.id)}
                  aria-current={parentActive ? 'page' : undefined}
                  aria-label={node.label}
                  className="group relative block w-full"
                >
                  {content}
                  <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    {node.label}
                  </span>
                </button>
              );
            }

            return (
              <div key={node.id} className="space-y-1">
                {node.href ? (
                  <Link
                    href={node.href}
                    data-testid={`nav-${node.id}`}
                    aria-current={parentActive ? 'page' : undefined}
                    className={[
                      'flex w-full items-center gap-3 rounded-xl border px-3 py-[10px] text-left transition-colors',
                      parentActive ? 'border-sidebar-border bg-card' : 'border-transparent hover:bg-sidebar-accent',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'flex h-9 w-9 items-center justify-center rounded-[10px]',
                        parentActive ? 'bg-chart-1/15' : 'bg-muted',
                      ].join(' ')}
                    >
                      <Icon className={['h-[18px] w-[18px]', parentActive ? 'text-chart-1' : 'text-muted-foreground'].join(' ')} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-sidebar-foreground">
                        {node.label}
                        {badge ? (
                          <span
                            className={[
                              'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold',
                              badge.intent === 'new' ? 'border-chart-2/25 bg-chart-2/10 text-chart-2' : 'border-border bg-muted text-muted-foreground',
                            ].join(' ')}
                          >
                            {badge.label}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{node.description}</div>
                    </div>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleSection(node.id)}
                    data-testid={`nav-${node.id}`}
                    className={[
                      'flex w-full items-center gap-3 rounded-xl border px-3 py-[10px] text-left transition-colors',
                      parentActive ? 'border-sidebar-border bg-card' : 'border-transparent hover:bg-sidebar-accent',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'flex h-9 w-9 items-center justify-center rounded-[10px]',
                        parentActive ? 'bg-chart-1/15' : 'bg-muted',
                      ].join(' ')}
                    >
                      <Icon className={['h-[18px] w-[18px]', parentActive ? 'text-chart-1' : 'text-muted-foreground'].join(' ')} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-sidebar-foreground">
                        {node.label}
                        {badge ? (
                          <span
                            className={[
                              'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold',
                              badge.intent === 'new'
                                ? 'border-chart-2/25 bg-chart-2/10 text-chart-2'
                                : 'border-border bg-muted text-muted-foreground',
                            ].join(' ')}
                          >
                            {badge.label}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{node.description}</div>
                    </div>
                    <ChevronDown
                      className={[
                        'h-4 w-4 text-muted-foreground transition-transform duration-200',
                        expandedSections.includes(node.id) ? 'rotate-180' : 'rotate-0',
                      ].join(' ')}
                    />
                  </button>
                )}

                {hasChildren && expandedSections.includes(node.id) ? (
                  <div className="ml-5 mt-1 border-l-2 border-sidebar-border pl-3">
                    <div className="space-y-1 py-1">
                      {node.children?.map((child) => {
                        const isChildActive = activeId === child.id;
                        const ChildIcon = child.icon;
                       const childBadge = child.badge;
                       const resolvedHref =
                         child.id === 'org-designer'
                           ? navHrefs.orgCanvasHref
                           : child.id === 'org-insights'
                           ? navHrefs.orgInsightsHref
                           : child.id === 'org-structure'
                           ? navHrefs.orgStructureHref
                           : child.id === 'executive-decision-memos'
                           ? navHrefs.decisionMemosHref
                           : child.id === 'executive-board-rollup'
                           ? navHrefs.boardRollupHref
                          : child.id === 'executive-briefs'
                          ? navHrefs.executiveBriefsHref
                          : child.id === 'strategy-compiler'
                          ? navHrefs.strategyCompilerHref
                          : child.id === 'strategy-blueprints'
                          ? navHrefs.blueprintRunsHref
                          : child.href;
                        const isDisabled = !resolvedHref;

                        return (
                          isDisabled ? (
                            <div
                              key={child.id}
                              data-testid={`nav-${child.id}`}
                              aria-disabled="true"
                              className="flex w-full items-center gap-2 rounded-lg px-2 py-2 opacity-60 cursor-not-allowed"
                              title="Coming soon"
                            >
                              <ChildIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="flex-1 text-[13px] font-medium text-muted-foreground">
                                {child.label}
                              </span>
                              <span className="inline-flex items-center rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
                                SOON
                              </span>
                            </div>
                          ) : (
                            <Link
                              key={child.id}
                              href={resolvedHref}
                              data-testid={`nav-${child.id}`}
                              aria-current={isChildActive ? 'page' : undefined}
                              className={[
                                'flex w-full items-center gap-2 rounded-lg px-2 py-2 transition-colors',
                                isChildActive ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent',
                              ].join(' ')}
                            >
                              <ChildIcon className={['h-4 w-4', isChildActive ? 'text-sidebar-foreground' : 'text-muted-foreground'].join(' ')} />
                              <span
                                className={[
                                  'flex-1 text-[13px]',
                                  isChildActive ? 'font-semibold text-sidebar-foreground' : 'font-medium text-muted-foreground',
                                ].join(' ')}
                              >
                                {child.label}
                              </span>
                              {childBadge ? (
                                <span
                                  className={[
                                    'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold',
                                    childBadge.intent === 'new'
                                      ? 'border-chart-2/25 bg-chart-2/10 text-chart-2'
                                      : 'border-border bg-muted text-muted-foreground',
                                  ].join(' ')}
                                >
                                  {childBadge.label}
                                </span>
                              ) : null}
                            </Link>
                          )
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className={effectiveCollapsed ? 'mt-4' : 'mt-6'}>
          {!effectiveCollapsed ? <div className="my-4 h-px bg-sidebar-border" /> : null}

          {isAdmin ? (
            <>
              {!effectiveCollapsed ? (
                <div
                  data-testid="nav-admin-section"
                  className="flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sidebar-border" />
                  Admin
                </div>
              ) : (
                <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <Shield className="h-4 w-4" />
                </div>
              )}

              <div className="space-y-1">
                {NAV_ADMIN.map((node) => {
                  const isActive = activeId === node.id;
                  const Icon = node.icon;

                  if (effectiveCollapsed) {
                    return (
                      <Link
                        key={node.id}
                        href={node.href ?? '#'}
                        data-testid={`nav-${node.id}`}
                        aria-current={isActive ? 'page' : undefined}
                        aria-label={node.label}
                        className={[
                          'group relative flex h-11 w-full items-center justify-center rounded-[10px] transition-colors',
                          isActive ? 'bg-card' : 'text-muted-foreground hover:bg-sidebar-accent',
                        ].join(' ')}
                      >
                        <Icon className={['h-5 w-5', isActive ? 'text-chart-1' : 'text-muted-foreground'].join(' ')} />
                        <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                          {node.label}
                        </span>
                      </Link>
                    );
                  }

                  return (
                    <Link
                      key={node.id}
                      href={node.href ?? '#'}
                      data-testid={`nav-${node.id}`}
                      aria-current={isActive ? 'page' : undefined}
                      className={[
                        'flex w-full items-center gap-3 rounded-xl border px-3 py-[10px] text-left transition-colors',
                        isActive ? 'border-sidebar-border bg-card' : 'border-transparent hover:bg-sidebar-accent',
                      ].join(' ')}
                    >
                      <div className={['flex h-9 w-9 items-center justify-center rounded-[10px]', isActive ? 'bg-chart-1/15' : 'bg-muted'].join(' ')}>
                        <Icon className={['h-[18px] w-[18px]', isActive ? 'text-chart-1' : 'text-muted-foreground'].join(' ')} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-sm font-semibold text-sidebar-foreground">{node.label}</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{node.description}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </nav>

      <div className={['border-t border-sidebar-border', effectiveCollapsed ? 'px-3 py-3' : 'px-4 py-4'].join(' ')}>
        {!effectiveCollapsed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-chart-1/10 text-sm font-semibold text-chart-1">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-sidebar-foreground">Account</div>
                <div className="text-[11px] text-muted-foreground">{isAdmin ? 'Admin' : 'Member'}</div>
              </div>
              <Link
                href="/settings"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent"
                aria-label="Settings"
                data-testid="nav-settings"
              >
                <Settings className="h-4 w-4" />
              </Link>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent disabled:opacity-50"
                aria-label="Sign out"
                data-testid="nav-sign-out"
                onClick={handleSignOut}
                disabled={signingOut}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
            {signOutError ? (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] text-red-700"
                data-testid="nav-sign-out-error"
                aria-live="polite"
              >
                {signOutError}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mx-auto flex flex-col items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-chart-1/10 text-sm font-semibold text-chart-1">
              {initials}
            </div>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-sidebar-accent disabled:opacity-50"
              aria-label="Sign out"
              data-testid="nav-sign-out"
              onClick={handleSignOut}
              disabled={signingOut}
            >
              <LogOut className="h-4 w-4" />
            </button>
            {signOutError ? (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-center text-[10px] text-red-700"
                data-testid="nav-sign-out-error"
                aria-live="polite"
              >
                {signOutError}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
